/**
 * LinkedAkteBanner
 * ----------------
 * Shows a cross-reference banner when an akte is linked to another akte in a
 * different business unit. Used to jump between a Gutachten-Akte and its
 * Mietwagen-Akte (and vice versa) — both halves of the typical accident
 * workflow: one side tracks the damage report, the other tracks the rental
 * during the repair.
 *
 * Linking is populated by `scripts/link-akten.cjs` via the shared Rentsoft
 * invoice number, which lives on `akten.rentsoft_invoice_nr` on both sides.
 * Once linked, `akten.linked_akte_id` points from each row to the other.
 *
 * Implementation notes:
 *
 *   - Uses a `useRef` guard (`lastFetchedId`) to make sure the Supabase call
 *     fires **once** per distinct `linkedAkteId`. Without it, a parent that
 *     re-creates its `akte` object on every re-render (e.g. after our realtime
 *     subscription bumps it) would cause the effect to re-fire → setState →
 *     re-render loop, which React flags as "Maximum update depth exceeded".
 *   - Rendered as a plain anchor (`<a href>`) + `wouter`'s `navigate`, not
 *     Radix's `<Slot>`/`asChild`, to avoid their ref-composition machinery
 *     interacting with the surrounding error-boundary tree.
 *   - Null-safe and bail-safe: if anything goes wrong fetching, we render
 *     nothing rather than propagating the error upward.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { Car, Key, ExternalLink, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { aktenIdentifier, buildAkteHref } from '@/lib/akten-link';

type BU = 'gutachten' | 'mietwagen' | string;

interface LinkedAkte {
  id: string;
  akten_nummer: string | null;
  akten_nr: string | null;
  kunde_name: string | null;
  business_unit: BU;
  kennzeichen: string | null;
  fahrzeug_marke: string | null;
  fahrzeug_modell: string | null;
  mietbeginn: string | null;
  rentsoft_invoice_nr: string | null;
}

export default function LinkedAkteBanner({
  linkedAkteId,
  currentBusinessUnit: _currentBusinessUnit,
}: {
  linkedAkteId: string | null | undefined;
  currentBusinessUnit: BU;
}) {
  const [linked, setLinked] = useState<LinkedAkte | null>(null);
  const [loading, setLoading] = useState(false);
  const [, navigate] = useLocation();
  // Tracks which linkedAkteId we've fetched for, so parent re-renders that
  // don't actually change the id don't re-fetch and re-setState.
  const lastFetchedId = useRef<string | null>(null);

  useEffect(() => {
    const id = linkedAkteId || null;
    if (lastFetchedId.current === id) return;
    lastFetchedId.current = id;

    if (!id) {
      setLinked(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await supabase
          .from('akten')
          .select('id, akten_nummer, akten_nr, kunde_name, business_unit, kennzeichen, fahrzeug_marke, fahrzeug_modell, mietbeginn, rentsoft_invoice_nr')
          .eq('id', id)
          .maybeSingle();
        if (cancelled) return;
        setLinked((data as LinkedAkte | null) ?? null);
      } catch {
        if (!cancelled) setLinked(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [linkedAkteId]);

  if (!linkedAkteId) return null;
  if (loading && !linked) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-[12px] text-gray-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Verknüpfte Akte wird geladen...
      </div>
    );
  }
  if (!linked) return null;

  // Prefer akten_nummer (authoritative NOT NULL) over the legacy nullable
  // akten_nr — without this the href can render as `/akten/gutachten/null/...`
  // whenever the legacy column is empty, producing a frontend error (issue #153).
  const displayIdentifier = aktenIdentifier(linked);
  if (!displayIdentifier) return null;

  const isLinkingToMietwagen = linked.business_unit === 'mietwagen';
  const IconCmp = isLinkingToMietwagen ? Key : Car;
  const color = isLinkingToMietwagen
    ? 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
    : 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100';
  const accentBg = isLinkingToMietwagen ? 'bg-amber-500' : 'bg-blue-600';
  const label = isLinkingToMietwagen ? 'Mietwagen-Akte' : 'Kfz-Gutachten-Akte';
  const vehicle = [linked.fahrzeug_marke, linked.fahrzeug_modell].filter(Boolean).join(' ');
  const summary = isLinkingToMietwagen
    ? vehicle + (linked.mietbeginn ? ` · Mietbeginn ${new Date(linked.mietbeginn).toLocaleDateString('de-DE')}` : '')
    : vehicle + (linked.kennzeichen ? ` · ${linked.kennzeichen}` : '');

  const targetHref = buildAkteHref(linked.business_unit, displayIdentifier);

  return (
    <a
      href={targetHref}
      onClick={(e) => { e.preventDefault(); navigate(targetHref); }}
      className={`flex items-center gap-3 px-3 sm:px-4 py-2 rounded-xl border transition-colors group cursor-pointer ${color}`}
    >
      <span className={`w-8 h-8 rounded-lg ${accentBg} text-white flex items-center justify-center flex-shrink-0`}>
        <IconCmp className="w-4 h-4" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">Verknüpft mit</span>
          <span className="text-[13px] font-bold">{label}</span>
          <span className="text-[12px] font-mono font-semibold opacity-90">{displayIdentifier}</span>
        </span>
        <span className="text-[12px] opacity-80 truncate block">
          {linked.kunde_name || '—'}
          {summary ? ` · ${summary}` : ''}
          {linked.rentsoft_invoice_nr && (
            <span className="ml-2 opacity-60">· RG {linked.rentsoft_invoice_nr}</span>
          )}
        </span>
      </span>
      <ExternalLink className="w-4 h-4 opacity-60 group-hover:opacity-100 flex-shrink-0" />
    </a>
  );
}
